import React, { forwardRef } from 'react';
import clsx from 'clsx';

// ── Button ────────────────────────────────────────────
export const Button = forwardRef(({
  children, variant = 'primary', size = 'md',
  loading = false, icon: Icon, iconRight, className, ...props
}, ref) => {
  const sizes   = { sm:'btn-sm', md:'btn-md', lg:'btn-lg' };
  const variants= { primary:'btn-primary', secondary:'btn-secondary', danger:'btn-danger', ghost:'btn-ghost', outline:'btn-outline' };

  return (
    <button ref={ref} className={clsx(sizes[size], variants[variant], className)} disabled={loading || props.disabled} {...props}>
      {loading
        ? <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
        : Icon && <Icon className="h-4 w-4" />}
      {children}
      {iconRight && !loading && <iconRight.type {...iconRight.props} className={clsx('h-4 w-4', iconRight.props?.className)} />}
    </button>
  );
});

// ── Input ─────────────────────────────────────────────
export const Input = forwardRef(({ label, error, hint, className, prefix, suffix, ...props }, ref) => (
  <div className="w-full">
    {label && <label className="label">{label}</label>}
    <div className="relative flex items-center">
      {prefix && <span className="absolute left-3.5 text-surface-400 pointer-events-none">{prefix}</span>}
      <input
        ref={ref}
        className={clsx('input', prefix && 'pl-9', suffix && 'pr-9', error && 'input-error', className)}
        {...props}
      />
      {suffix && <span className="absolute right-3.5 text-surface-400">{suffix}</span>}
    </div>
    {error && <p className="mt-1 text-xs text-danger-600">{error}</p>}
    {hint  && !error && <p className="mt-1 text-xs text-surface-400">{hint}</p>}
  </div>
));

// ── Select ────────────────────────────────────────────
export const Select = forwardRef(({ label, error, className, children, ...props }, ref) => (
  <div className="w-full">
    {label && <label className="label">{label}</label>}
    <select ref={ref} className={clsx('input appearance-none cursor-pointer', error && 'input-error', className)} {...props}>
      {children}
    </select>
    {error && <p className="mt-1 text-xs text-danger-600">{error}</p>}
  </div>
));

// ── Textarea ──────────────────────────────────────────
export const Textarea = forwardRef(({ label, error, className, ...props }, ref) => (
  <div className="w-full">
    {label && <label className="label">{label}</label>}
    <textarea ref={ref} className={clsx('input min-h-[80px] resize-y', error && 'input-error', className)} {...props} />
    {error && <p className="mt-1 text-xs text-danger-600">{error}</p>}
  </div>
));

// ── Badge ─────────────────────────────────────────────
const BADGE_VARIANTS = {
  green: 'badge-green', red: 'badge-red', yellow: 'badge-yellow',
  blue: 'badge-blue', purple: 'badge-purple', gray: 'badge-gray',
};
export const Badge = ({ children, variant = 'gray', dot = false, className }) => (
  <span className={clsx(BADGE_VARIANTS[variant] || 'badge-gray', className)}>
    {dot && <span className={clsx('w-1.5 h-1.5 rounded-full', {
      'bg-success-500': variant === 'green', 'bg-danger-500': variant === 'red',
      'bg-warning-500': variant === 'yellow', 'bg-info-500': variant === 'blue',
      'bg-brand-500': variant === 'purple', 'bg-surface-400': variant === 'gray',
    })} />}
    {children}
  </span>
);

// ── Spinner ───────────────────────────────────────────
export const Spinner = ({ size = 'md', className }) => {
  const sizes = { sm: 'h-4 w-4', md: 'h-6 w-6', lg: 'h-8 w-8', xl: 'h-12 w-12' };
  return (
    <svg className={clsx(sizes[size], 'animate-spin text-brand-600', className)} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
};

// ── Loading page ──────────────────────────────────────
export const LoadingPage = () => (
  <div className="flex h-full min-h-[300px] items-center justify-center">
    <Spinner size="lg" />
  </div>
);

// ── Empty State ───────────────────────────────────────
export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    {Icon && (
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-100">
        <Icon className="h-7 w-7 text-surface-400" />
      </div>
    )}
    <h3 className="mb-1 font-semibold text-surface-800">{title}</h3>
    {description && <p className="mb-4 max-w-xs text-sm text-surface-500">{description}</p>}
    {action}
  </div>
);

// ── Card ──────────────────────────────────────────────
export const Card = ({ children, className, padding = true }) => (
  <div className={clsx('card', padding && 'p-5', className)}>{children}</div>
);

// ── Modal ─────────────────────────────────────────────
export const Modal = ({ open, onClose, title, children, size = 'md', footer }) => {
  if (!open) return null;
  const sizes = { sm:'max-w-sm', md:'max-w-lg', lg:'max-w-2xl', xl:'max-w-4xl', full:'max-w-6xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className={clsx('relative z-10 w-full rounded-2xl bg-white shadow-2xl animate-in flex flex-col max-h-[90vh]', sizes[size])}>
        {title && (
          <div className="flex items-center justify-between border-b border-surface-100 px-6 py-4 flex-shrink-0">
            <h2 className="font-display text-base font-semibold text-surface-900">{title}</h2>
            <button onClick={onClose} className="btn-ghost rounded-lg p-1.5 btn">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        )}
        <div className="px-6 py-5 overflow-y-auto flex-1 min-h-0">{children}</div>
        {footer && <div className="border-t border-surface-100 px-6 py-4 flex-shrink-0">{footer}</div>}
      </div>
    </div>
  );
};

// ── Confirm Dialog ────────────────────────────────────
export const ConfirmDialog = ({ open, onClose, onConfirm, title, message, confirmText = 'Confirm', danger = false, loading = false }) => (
  <Modal open={open} onClose={onClose} title={title} size="sm">
    <p className="text-sm text-surface-600">{message}</p>
    <div className="mt-5 flex justify-end gap-3">
      <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
      <Button variant={danger ? 'danger' : 'primary'} size="sm" onClick={onConfirm} loading={loading}>{confirmText}</Button>
    </div>
  </Modal>
);

// ── Avatar ────────────────────────────────────────────
export const Avatar = ({ name = '', src, size = 'md', className }) => {
  const [imgError, setImgError] = React.useState(false);
  const sizes = { sm:'h-7 w-7 text-xs', md:'h-9 w-9 text-sm', lg:'h-11 w-11 text-base', xl:'h-14 w-14 text-lg' };
  const initials = name.split(' ').map(n => n[0]).join('').substring(0,2).toUpperCase();
  if (src && !imgError) return <img src={src} alt={name} onError={() => setImgError(true)} className={clsx('rounded-full object-cover', sizes[size], className)} />;
  return <div className={clsx('avatar flex-shrink-0', sizes[size], className)}>{initials || '?'}</div>;
};

// ── Stats card ────────────────────────────────────────
export const StatCard = ({ label, value, change, icon: Icon, color = 'blue', prefix = '' }) => {
  const colors = {
    blue  : 'bg-info-50 text-info-600',
    green : 'bg-success-50 text-success-600',
    purple: 'bg-brand-50 text-brand-600',
    orange: 'bg-warning-50 text-warning-600',
    red   : 'bg-danger-50 text-danger-600',
  };
  const isPositive = change >= 0;
  return (
    <div className="stat-card">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-surface-500">{label}</span>
        {Icon && <div className={clsx('flex h-9 w-9 items-center justify-center rounded-xl', colors[color])}><Icon className="h-4.5 w-4.5" /></div>}
      </div>
      <div>
        <div className="font-display text-2xl font-bold text-surface-900">{prefix}{value?.toLocaleString?.() ?? value}</div>
        {change !== undefined && (
          <p className={clsx('mt-1 text-xs font-medium', isPositive ? 'text-success-600' : 'text-danger-600')}>
            {isPositive ? '↑' : '↓'} {Math.abs(change)}% vs last month
          </p>
        )}
      </div>
    </div>
  );
};

// ── Pagination ────────────────────────────────────────
export const Pagination = ({ page, pages, onPageChange }) => {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-1.5 pt-4">
      <Button variant="outline" size="sm" disabled={page === 1}  onClick={() => onPageChange(page - 1)}>← Prev</Button>
      {Array.from({ length: Math.min(7, pages) }, (_, i) => {
        const p = i + 1;
        return (
          <button key={p} onClick={() => onPageChange(p)}
            className={clsx('h-8 w-8 rounded-lg text-sm font-medium transition-colors',
              p === page ? 'bg-brand-600 text-white' : 'text-surface-600 hover:bg-surface-100')}>
            {p}
          </button>
        );
      })}
      <Button variant="outline" size="sm" disabled={page === pages} onClick={() => onPageChange(page + 1)}>Next →</Button>
    </div>
  );
};

// ── Search input ──────────────────────────────────────
export const SearchInput = ({ value, onChange, placeholder = 'Search...', className }) => (
  <div className={clsx('relative', className)}>
    <svg className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-surface-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
    <input
      type="text" value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="input pl-9"
    />
  </div>
);

// ── Status badge helper ───────────────────────────────
const STATUS_MAP = {
  approved: 'green', active: 'green', delivered: 'green', captured: 'green', completed: 'green', published: 'green',
  pending: 'yellow', pending_review: 'yellow', processing: 'yellow', queued: 'yellow', pickup_scheduled: 'yellow',
  rejected: 'red', suspended: 'red', cancelled: 'red', failed: 'red', blocked: 'red',
  shipped: 'blue', confirmed: 'blue', in_progress: 'blue', return_requested: 'blue', return_approved: 'blue',
  refunded: 'purple', return_picked: 'purple', picked_up: 'purple',
};
export const StatusBadge = ({ status }) => (
  <Badge variant={STATUS_MAP[status] || 'gray'} dot>
    {status?.replace(/_/g, ' ')}
  </Badge>
);

// ── Section header ────────────────────────────────────
export const SectionHeader = ({ title, subtitle, action }) => (
  <div className="mb-6 flex items-start justify-between gap-4">
    <div>
      <h1 className="page-title">{title}</h1>
      {subtitle && <p className="mt-0.5 text-sm text-surface-500">{subtitle}</p>}
    </div>
    {action && <div className="flex-shrink-0">{action}</div>}
  </div>
);

// ── Toggle ────────────────────────────────────────────
export const Toggle = ({ checked, onChange, label, disabled }) => (
  <label className={clsx('flex cursor-pointer items-center gap-3', disabled && 'opacity-50 cursor-not-allowed')}>
    <div className="relative">
      <input type="checkbox" className="sr-only" checked={checked} onChange={onChange} disabled={disabled} />
      <div className={clsx('h-5 w-9 rounded-full transition-colors', checked ? 'bg-brand-600' : 'bg-surface-200')} />
      <div className={clsx('absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform', checked && 'translate-x-4')} />
    </div>
    {label && <span className="text-sm font-medium text-surface-700">{label}</span>}
  </label>
);

// ── Tabs ──────────────────────────────────────────────
export const Tabs = ({ tabs, active, onChange }) => (
  <div className="flex gap-1 rounded-xl bg-surface-100 p-1">
    {tabs.map(tab => (
      <button key={tab.value} onClick={() => onChange(tab.value)}
        className={clsx('flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-all',
          active === tab.value ? 'bg-white text-surface-900 shadow-card' : 'text-surface-500 hover:text-surface-700')}>
        {tab.label}
        {tab.count !== undefined && <span className={clsx('ml-1.5 rounded-full px-1.5 py-0.5 text-xs', active === tab.value ? 'bg-brand-100 text-brand-700' : 'bg-surface-200 text-surface-500')}>{tab.count}</span>}
      </button>
    ))}
  </div>
);

// ── High-Risk Action Confirmation Modal ────────────────
export const HighRiskActionModal = ({
  open,
  onClose,
  onConfirm,
  title = 'High-Risk Action Confirmation',
  entitySummary,
  warningMessage = 'This action is high-risk and will be recorded in the immutable audit trail.',
  confirmLabel = 'Confirm Action',
  danger = true,
  loading = false,
}) => {
  const [reason, setReason] = React.useState('');
  const [confirmed, setConfirmed] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setReason('');
      setConfirmed(false);
    }
  }, [open]);

  const isValid = reason.trim().length >= 10 && confirmed;

  const handleExecute = () => {
    if (!isValid || loading) return;
    const idempotencyKey = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `idemp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    onConfirm({ reason: reason.trim(), idempotencyKey });
  };

  return (
    <Modal open={open} onClose={onClose} title={title} size="md">
      <div className="space-y-4 text-sm">
        {/* Warning Banner */}
        <div className={clsx('p-3.5 rounded-xl border flex gap-3', danger ? 'bg-danger-50 border-danger-200 text-danger-800' : 'bg-warning-50 border-warning-200 text-warning-800')}>
          <span className="text-lg">⚠️</span>
          <div>
            <p className="font-semibold">{danger ? 'Critical Operation' : 'Important Notice'}</p>
            <p className="text-xs mt-0.5 opacity-90">{warningMessage}</p>
          </div>
        </div>

        {/* Entity Summary */}
        {entitySummary && (
          <div className="rounded-xl bg-surface-50 border border-surface-200 p-3 text-xs text-surface-700">
            {entitySummary}
          </div>
        )}

        {/* Mandatory Reason */}
        <div>
          <label className="label">
            Mandatory Justification / Reason <span className="text-danger-500">* (min 10 chars)</span>
          </label>
          <textarea
            className="input min-h-[80px] text-xs resize-y"
            placeholder="Explain why this action is being taken (recorded in audit log)..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={loading}
          />
          <div className="flex justify-between items-center mt-1 text-[11px] text-surface-400">
            <span>Minimum 10 characters required</span>
            <span className={reason.trim().length >= 10 ? 'text-success-600 font-medium' : 'text-surface-400'}>
              {reason.trim().length}/10
            </span>
          </div>
        </div>

        {/* Checkbox Acknowledgment */}
        <label className="flex items-start gap-2.5 cursor-pointer pt-1">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            disabled={loading}
            className="mt-0.5 rounded border-surface-300 text-brand-600 focus:ring-brand-500"
          />
          <span className="text-xs text-surface-600 select-none">
            I understand this action modifies platform state and will be immutably recorded under my Admin ID.
          </span>
        </label>

        {/* Actions */}
        <div className="pt-3 border-t border-surface-100 flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            size="sm"
            onClick={handleExecute}
            disabled={!isValid}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

